import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, Download, Clock, ExternalLink, CheckCircle, AlertTriangle, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('crm_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_OPTIONS = [
  { value: 'pending',   label: 'Pending',    cls: 'bg-blue-100 text-blue-800' },
  { value: 'paid',      label: 'Bezahlt',    cls: 'bg-green-100 text-green-800' },
  { value: 'cancelled', label: 'Abgebrochen', cls: 'bg-gray-200 text-gray-500' },
  { value: 'failed',    label: 'Fehlgeschlagen', cls: 'bg-red-100 text-red-700' },
  { value: 'expired',   label: 'Abgelaufen', cls: 'bg-gray-200 text-gray-500' },
  { value: 'refunded',  label: 'Erstattet',  cls: 'bg-orange-100 text-orange-700' },
];

const MODULE_LABELS = {
  'ready-check': 'KI Ready Check',
  'strategy': 'KI Strategie Workshop',
  'integration': 'KI Integration',
};

function StatusPill({ value }) {
  const opt = STATUS_OPTIONS.find((o) => o.value === value) || { label: value || '—', cls: 'bg-gray-100 text-gray-600' };
  return <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${opt.cls}`}>{opt.label}</span>;
}

function formatGermanDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

function exportCSV(orders) {
  const header = 'Eingang;Status;Bezahlt;Modul;Betrag;Name;Email;Unternehmen;USt-IdNr;Telefon;Methode;Rechnungs-Nr;Order-ID\n';
  const rows = orders.map((o) => {
    const safe = (v) => `"${String(v || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
    return [
      new Date(o.created_at).toLocaleString('de-DE'),
      o.status,
      o.paid_at ? new Date(o.paid_at).toLocaleString('de-DE') : '',
      MODULE_LABELS[o.module] || o.module,
      o.amount_eur,
      safe(o.customer_name), safe(o.customer_email), safe(o.customer_company),
      safe(o.customer_vat_id), safe(o.customer_phone),
      safe(o.mollie_method), safe(o.invoice_number),
      o.id,
    ].join(';');
  }).join('\n');
  const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `bestellungen-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [activeOrder, setActiveOrder] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: authHeaders() });
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = orders.filter((o) => filter === 'all' ? true : o.status === filter);
  const totalPaidEur = orders
    .filter((o) => o.status === 'paid')
    .reduce((sum, o) => sum + parseFloat(o.amount_eur || 0), 0);

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1f1f1f] mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Bestellungen
          </h1>
          <p className="text-sm text-gray-500">Mollie-Checkout aus Modul-1- und Modul-2-Landings</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 border border-gray-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-gray-50">
            <RefreshCw size={14} /> Neu laden
          </button>
          <button onClick={() => exportCSV(orders)} disabled={!orders.length}
            className="inline-flex items-center gap-2 border border-gray-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-gray-50 disabled:opacity-40">
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Stat label="Gesamt" value={orders.length} />
        <Stat label="Bezahlt" value={orders.filter((o) => o.status === 'paid').length} color="text-[#658a07]" />
        <Stat label="Pending" value={orders.filter((o) => o.status === 'pending').length} />
        <Stat label="Umsatz bezahlt" value={`${totalPaidEur.toLocaleString('de-DE')} €`} color="text-[#1f1f1f]" />
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 mb-4 text-sm"><AlertTriangle size={14} className="inline mr-2" /> {error}</div>}

      {/* Filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter('all')}
          className={`text-xs uppercase tracking-wider px-3 py-1.5 border transition-colors ${filter === 'all' ? 'bg-[#0a0a0a] text-[#befa0a] border-[#0a0a0a]' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          style={{ fontFamily: "'Oswald', sans-serif" }}>
          Alle ({orders.length})
        </button>
        {STATUS_OPTIONS.map((o) => {
          const count = orders.filter((x) => x.status === o.value).length;
          if (count === 0 && o.value !== 'paid' && o.value !== 'pending') return null;
          return (
            <button key={o.value} onClick={() => setFilter(o.value)}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 border transition-colors ${filter === o.value ? 'bg-[#0a0a0a] text-[#befa0a] border-[#0a0a0a]' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {o.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="p-12 text-center text-gray-400">Lade…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-gray-300 p-12 text-center text-gray-400">
          Keine Bestellungen{filter !== 'all' ? ` mit Status "${filter}"` : ''}.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2.5">Eingang</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Modul</th>
                <th className="px-4 py-2.5 text-right">Betrag</th>
                <th className="px-4 py-2.5">Kunde</th>
                <th className="px-4 py-2.5">Methode</th>
                <th className="px-4 py-2.5">Rechnung</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setActiveOrder(o)}>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    <Clock size={11} className="inline mr-1 opacity-50" />
                    {formatGermanDate(o.created_at)}
                  </td>
                  <td className="px-4 py-3"><StatusPill value={o.status} /></td>
                  <td className="px-4 py-3 text-xs">{MODULE_LABELS[o.module] || o.module}</td>
                  <td className="px-4 py-3 text-right font-bold">{parseFloat(o.amount_eur).toLocaleString('de-DE')} €</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#1f1f1f]">{o.customer_name}</div>
                    <div className="text-xs text-gray-500">{o.customer_company || '—'} · {o.customer_email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{o.mollie_method || '—'}</td>
                  <td className="px-4 py-3 text-xs">
                    {o.invoice_number ? (
                      <span className="text-[#658a07] flex items-center gap-1"><CheckCircle size={11} /> {o.invoice_number}</span>
                    ) : o.status === 'paid' ? (
                      <span className="text-[#e07a2f]">offen</span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-[#658a07] text-xs uppercase tracking-wider font-bold">Details →</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeOrder && <OrderDetailModal order={activeOrder} onClose={() => setActiveOrder(null)} onUpdate={(updated) => { setOrders((arr) => arr.map((x) => x.id === updated.id ? { ...x, ...updated } : x)); setActiveOrder((cur) => cur ? { ...cur, ...updated } : cur); }} />}
    </div>
  );
}

function OrderDetailModal({ order, onClose, onUpdate }) {
  const [invoiceNumber, setInvoiceNumber] = useState(order.invoice_number || '');
  const [notes, setNotes] = useState(order.notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (invoiceNumber !== order.invoice_number) {
        payload.invoice_number = invoiceNumber;
        if (invoiceNumber) payload.invoiced_at = new Date().toISOString();
      }
      if (notes !== order.notes) payload.notes = notes;
      if (Object.keys(payload).length > 0) {
        await axios.patch(`${API}/admin/orders/${order.id}`, payload, { headers: authHeaders() });
        onUpdate({ ...payload, id: order.id });
      }
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-[680px] max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="bg-[#0a0a0a] text-white px-6 py-4 flex justify-between items-start">
          <div>
            <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Order-Details
            </p>
            <h3 className="text-lg font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {MODULE_LABELS[order.module]} · {parseFloat(order.amount_eur).toLocaleString('de-DE')} €
            </h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Status-Reihe */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat label="Status" value={<StatusPill value={order.status} />} small />
            <Stat label="Eingang" value={formatGermanDate(order.created_at)} small />
            <Stat label="Bezahlt am" value={order.paid_at ? formatGermanDate(order.paid_at) : '—'} small color={order.paid_at ? 'text-[#658a07]' : 'text-gray-400'} />
          </div>

          {/* Customer */}
          <div className="border border-gray-200 p-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Kunde</p>
            <p className="text-[#1f1f1f] font-bold mb-1">{order.customer_name}</p>
            <p className="text-sm text-gray-600 mb-0.5">{order.customer_company || '—'}</p>
            <p className="text-sm"><a href={`mailto:${order.customer_email}`} className="text-[#658a07] hover:underline">{order.customer_email}</a></p>
            {order.customer_phone && <p className="text-sm text-gray-600">{order.customer_phone}</p>}
            {order.customer_vat_id && <p className="text-xs text-gray-500 mt-1">USt-IdNr: <code>{order.customer_vat_id}</code></p>}
          </div>

          {/* Mollie */}
          <div className="border border-gray-200 p-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Mollie</p>
            <p className="text-xs text-gray-600 mb-1">Payment-ID: <code>{order.mollie_payment_id || '—'}</code></p>
            <p className="text-xs text-gray-600 mb-2">Methode: {order.mollie_method || '—'}</p>
            {order.mollie_checkout_url && (
              <a href={order.mollie_checkout_url} target="_blank" rel="noopener noreferrer" className="text-[#658a07] text-xs hover:underline inline-flex items-center gap-1">
                Mollie-Checkout-Link <ExternalLink size={11} />
              </a>
            )}
          </div>

          {/* Rechnung */}
          <div className="border border-amber-300 bg-amber-50/40 p-4">
            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Rechnung (manuell erstellen + hier eintragen)
            </p>
            <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="z.B. 2026-042"
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
            {order.invoiced_at && (
              <p className="text-[11px] text-gray-500 mt-1">Eingetragen: {formatGermanDate(order.invoiced_at)}</p>
            )}
          </div>

          {/* Notizen */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Interne Notizen</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-none" />
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>Schließen</button>
            <button onClick={save} disabled={saving}
              className="bg-[#0a0a0a] text-[#befa0a] px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {saving ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color, small }) {
  return (
    <div className="border border-gray-200 p-4">
      <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">{label}</p>
      <p className={`${small ? 'text-base' : 'text-2xl'} font-bold ${color || 'text-[#1f1f1f]'}`} style={{ fontFamily: "'Oswald', sans-serif" }}>{value}</p>
    </div>
  );
}
